import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Product } from "@/lib/types";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/auth";

export const useProducts = () => {
  const [products, setProducts] = useState<Product[]>([]);
  const [groups, setGroups] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [stockUpdateDialogOpen, setStockUpdateDialogOpen] = useState(false);
  const [usageDialogOpen, setUsageDialogOpen] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user } = useAuth();

  useEffect(() => {
    const loadData = async () => {
      try {
        setIsLoading(true);
        
        if (!user) {
          console.log('No authenticated user, redirecting to auth page');
          navigate("/auth");
          return;
        }

        console.log(`Products page loaded for user: ${user.email} (${user.id})`);
        
        // Import storage dynamically to avoid circular dependencies
        const storage = await import('@/lib/storage');
        
        // Try to load products from both sources
        try {
          // The improved fetchProducts now properly merges local and remote data
          const remoteProducts = await storage.fetchProducts(true);
          const remoteGroups = await storage.fetchGroups(true);
          
          console.log(`Loaded ${remoteProducts.length} products after merging local and remote`);
          setProducts(remoteProducts);
          setGroups(remoteGroups);
        } catch (error) {
          console.error("Error loading remote data:", error);
          
          // If remote fails, use local data as fallback
          const localProducts = storage.getProducts();
          console.log(`Using ${localProducts.length} local products as fallback after remote error`);
          setProducts(localProducts);
          setGroups(storage.getGroups());
          
          toast({
            title: "Network Error",
            description: "Using local data. Some features may be limited.",
            variant: "destructive",
          });
        }
      } finally {
        setIsLoading(false);
      }
    };
    
    loadData();
  }, [navigate, toast, refreshKey, user]);

  const refreshData = () => {
    console.log("Refreshing product data");
    setRefreshKey(prevKey => prevKey + 1);
  };

  return {
    products,
    groups,
    isLoading,
    selectedProduct,
    setSelectedProduct,
    stockUpdateDialogOpen,
    setStockUpdateDialogOpen,
    usageDialogOpen,
    setUsageDialogOpen,
    refreshData
  };
};
